import { FormProvider, useForm } from 'react-hook-form';
import { useSpreedlyHosted } from '../spreedly-payment-hosted.hooks';
import { useURLSearchParams } from 'hooks/useURLSearchParams';
import { useMobileAppListeners } from 'hooks/mobileAppListeners/useMobileAppListeners';
import { NewPaymentProvider } from 'components/payment/payment.context';
import PaymentErrorBoundary from 'components/payment/payment-error-boundary/payment-error-boundary.component';
import SpreedlyPaymentHostedWrapper from '../spreedly-payment-hosted-wrapper.component';
import SpreedlyCheckoutSdkCvvRecacheForm from 'components/payment/spreedly-checkout-sdk/spreedly-card/forms/spreedly-checkout-sdk-cvv-recache-form/spreedly-checkout-sdk-cvv-recache-form.component';
import { SESSION_EXPIRED_ERROR_KEY } from 'components/payment/spreedly-checkout-sdk/spreedly-card/forms/spreedly-checkout-sdk-form.hooks';
import { getRecacheError } from 'components/payment/spreedly/spreedly-card/forms/spreedly-cvv-recache-form/spreedly-cvv-recache-form.utils';

// useSpreedlyHosted() throws on a script-loading failure (see its own
// comment) — it has to run inside PaymentErrorBoundary's subtree, not in the
// component that renders the boundary itself, or the throw would escape
// uncaught.
const SpreedlyCVVRecacheHostedContent = () => {
  const {
    initOrderResponse,
    loading,
    setLoading,
    spreedlyScriptLoading,
    refetchInitOrder,
  } = useSpreedlyHosted();

  const urlSearchParams = useURLSearchParams();

  const paymentInstrument = {
    paymentToken: urlSearchParams.get('paymentToken'),
    cardType: urlSearchParams.get('cardType'),
    lastFour: urlSearchParams.get('lastFour'),
  };

  const fireListeners = useMobileAppListeners();

  const onSuccess = () => {
    fireListeners('CVV Verification success');
    setLoading(false);
  };

  const onError = errors => {
    // The Checkout SDK routes its own local validation (e.g. a blank/invalid
    // CVV at submit time) through this same onError as a plain string,
    // normalized to `{message, attribute, clientSide: true}` with no `key`
    // (toSpreedlyCheckoutSdkErrorArray) — nothing was ever sent to Spreedly
    // for that case, and legacy's recache submit never reached the server
    // for it either (it stopped at validate() and never called
    // window.Spreedly.recache() at all), so it never notified the app.
    //
    // `clientSide: true` alone isn't enough to detect that, though: a real
    // timestamp-expired *server* rejection of an actual recache() attempt
    // also gets synthesized back through here as buildSessionExpiredError()
    // — tagged clientSide too (so a later computeRequiresSessionReset check
    // doesn't double-refetch), but it's a genuine, actionable server
    // failure the app needs to hear about, unlike a plain validation
    // string. Narrowly except just that one synthesized error rather than
    // any keyed clientSide error — the *other* synthesized one
    // (cvv_expired, the SDK's own unprompted 3-minute auto-clear) has no
    // legacy equivalent at all, so the legacy route never sent it to the
    // app either, and it should stay silent here too.
    const notifiableErrors = errors.filter(
      error => !error.clientSide || error.key === SESSION_EXPIRED_ERROR_KEY,
    );
    if (notifiableErrors.length > 0)
      fireListeners(getRecacheError(notifiableErrors));
    setLoading(false);
  };

  const methods = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  return (
    <SpreedlyPaymentHostedWrapper isLoading={spreedlyScriptLoading}>
      <FormProvider {...methods}>
        <NewPaymentProvider
          value={{ initOrderResponse, refetchInitOrder, loading }}
        >
          {!!initOrderResponse && (
            <SpreedlyCheckoutSdkCvvRecacheForm
              paymentInstrument={paymentInstrument}
              initOrderAttributes={initOrderResponse?.attributes}
              onSuccess={onSuccess}
              onError={onError}
              setLoading={setLoading}
              isLoading={loading}
            />
          )}
        </NewPaymentProvider>
      </FormProvider>
    </SpreedlyPaymentHostedWrapper>
  );
};

const SpreedlyCVVRecacheHosted = () => {
  return (
    <PaymentErrorBoundary name="spreedly-cvv-recache-hosted">
      <SpreedlyCVVRecacheHostedContent />
    </PaymentErrorBoundary>
  );
};

export default SpreedlyCVVRecacheHosted;
